/* eslint-disable max-len */
import type { Route } from 'nextjs-routes';

// Default OG description
const DEFAULT_TEMPLATE = '%network_name% Explorer - Search transactions, verify smart contracts, analyze addresses, and track network activity on the %network_title% network.';

const TEMPLATE_MAP: Record<Route['pathname'], string> = {
  // ── Homepage ──
  '/': '%network_name% Explorer is a blockchain explorer for the %network_title% network. Search transactions, addresses, tokens, and other activities on the chain.',

  // ── Transactions ──
  '/txs': 'View all transactions on the %network_title% network. Track confirmed, pending, and internal transaction activity in real time.',
  '/pending-txs': 'View pending transactions waiting to be confirmed on the %network_title% network.',
  '/internal-txs': 'View contract internal transactions (contract-to-contract calls) on the %network_title% network.',
  '/txs/kettle/[hash]': 'View transactions for kettle %hash% on the %network_title% network.',
  '/tx/[hash]': 'View details for transaction %hash% on %network_title% including status, gas used, block confirmation, and token transfers.',

  // ── Blocks ──
  '/blocks': 'View the latest blocks mined on the %network_title% network including block height, miner, transactions count, and gas usage.',
  '/forked-blocks': 'View forked blocks (chain reorganizations) on the %network_title% network.',
  '/uncle-blocks': 'View uncle blocks on the %network_title% network.',
  '/block/[height_or_hash]': 'View transactions, token transfers, and details for block %height_or_hash% on the %network_title% network.',
  '/block/countdown': 'Estimate the time remaining until a target block is mined on %network_title%.',
  '/block/countdown/[height]': 'Countdown to block %height% on the %network_title% network. View estimated time and current progress.',

  // ── Accounts & Addresses ──
  '/accounts': 'View top accounts ranked by balance on the %network_title% network. Explore whale wallets and top holders.',
  '/accounts/label/[slug]': 'Search addresses by label "%slug%" on the %network_title% network.',
  '/address/[hash]': 'View balance, transactions, token holdings, and contract details for address %hash% on the %network_title% network.',
  '/address/[hash]/contract-verification': 'Verify the smart contract source code at address %hash% on the %network_title% network.',

  // ── Contracts ──
  '/verified-contracts': 'Browse all verified smart contracts on the %network_title% network. Search by name, address, or compiler version.',
  '/contract-verification': 'Verify and publish your smart contract source code on %network_title% to enable public read/write access.',

  // ── Tokens ──
  '/tokens': 'View all ERC-20, ERC-721, and ERC-1155 tokens on the %network_title% network. Track token prices, holders, and transfers.',
  '/token/[hash]': 'View token details, holders, transfers, and analytics for token %hash% on the %network_title% network.',
  '/token/[hash]/instance/[id]': 'View NFT instance #%id% details including metadata, ownership history, and transfers on %network_title%.',
  '/token-transfers': 'View all token transfers across the %network_title% network including ERC-20, ERC-721, and ERC-1155 transfers.',
  '/token-flow': 'Visualize token holder distribution and flow patterns on the %network_title% network.',

  // ── DApps ──
  '/apps': 'Explore decentralized applications (DApps) built on the %network_title% network.',
  '/apps/[id]': 'View details and interact with this DApp on the %network_title% network.',

  // ── Stats & Charts ──
  '/stats': 'View comprehensive statistics and analytics for the %network_title% network including daily transactions, gas usage, and more.',
  '/stats/[id]': 'View detailed chart and statistics for %id% on the %network_title% network.',
  '/charts-stats': 'Explore comprehensive blockchain charts and statistics for the %network_title% network.',
  '/top-statistics': 'View top statistics including transactions, tokens, gas usage, and hot contracts on the %network_title% network.',
  '/leaderboard': 'Browse top advanced filters and community-created leaderboards on %network_title%.',

  // ── API & Dev Tools ──
  '/api-docs': 'Complete API documentation for the %network_title% network. Build applications with REST API endpoints.',
  '/graphiql': 'Interactive GraphQL playground to query blockchain data on the %network_title% network.',
  '/api-plans': 'View API pricing plans for the %network_title% network. Get started with blockchain data access.',

  // ── Search ──
  '/search-results': 'Search results for "%q%" on the %network_title% network.',

  // ── User Account ──
  '/auth/profile': 'Manage your %network_name% Explorer profile settings and preferences.',
  '/auth/verify': 'Verify your email address to complete %network_name% Explorer account registration.',
  '/auth/verify-email-change': 'Confirm your email address change for your %network_name% Explorer account.',
  '/account/overview': 'View your %network_name% Explorer account overview including activity and settings.',
  '/account/settings': 'Manage your %network_name% Explorer account settings, notifications, and preferences.',
  '/account/profile': 'Update your public profile on %network_name% Explorer including avatar, bio, and social links.',
  '/account/merits': 'View and track your merits and rewards on %network_name% Explorer.',
  '/account/watchlist': 'Manage your address watch list. Get email notifications for transactions and token transfers.',
  '/account/api-key': 'Manage your API keys for accessing %network_title% blockchain data programmatically.',
  '/account/custom-abi': 'Add custom ABIs to interact with unverified smart contracts on %network_title%.',
  '/account/tag-address': 'Create private address tags for quick identification of addresses on %network_title%.',
  '/account/name-tags': 'Manage your private name tags for addresses on %network_title% Explorer.',
  '/account/txn-notes': 'Add private notes to transactions on %network_title% for your personal reference.',
  '/account/token-ignore': 'Manage your token ignore list. Hidden tokens will not appear in holdings, transfers, or watch list.',
  '/account/api-dashboard': 'View your API usage statistics and manage API access on %network_name% Explorer.',
  '/account/advanced-filter': 'Save and manage custom advanced filters for blockchain data queries.',
  '/account/verified-addresses': 'View and manage your verified addresses linked to your %network_name% Explorer account.',

  // ── Auth ──
  '/signin': 'Sign in to your %network_name% Explorer account to access watchlists, tags, and more.',
  '/signup': 'Create a free %network_name% Explorer account. Access watchlists, private tags, API keys, and more.',
  '/login': 'Login to your %network_name% Explorer account.',
  '/forgot-password': 'Reset your %network_name% Explorer account password.',

  // ── Admin ──
  '/admin': 'Admin dashboard for managing the %network_name% Explorer platform.',
  '/admin/users': 'Manage registered users on %network_name% Explorer.',
  '/admin/tokens': 'Manage token listings and metadata on %network_name% Explorer.',
  '/admin/contracts': 'Review and manage verified contracts on %network_name% Explorer.',
  '/admin/contacts': 'View and manage contact form submissions on %network_name% Explorer.',
  '/admin/settings': 'Configure platform settings for %network_name% Explorer.',
  '/admin/labels': 'Manage address labels and tags on %network_name% Explorer.',
  '/admin/name-tags': 'Manage public name tags for addresses on %network_name% Explorer.',
  '/admin/reports': 'View platform reports and analytics for %network_name% Explorer.',
  '/admin/approvals': 'Review pending approval requests on %network_name% Explorer.',
  '/admin/api': 'Manage API configurations for %network_name% Explorer.',
  '/admin/ads': 'Manage advertisements on %network_name% Explorer.',
  '/admin/ad-submissions': 'View and manage ad submissions on %network_name% Explorer.',
  '/admin/node-management': 'Manage API nodes and infrastructure for %network_name% Explorer.',
  '/admin/user-activities': 'View user activity and audit logs on %network_name% Explorer.',

  // ── Public Tags ──
  '/public-tags/submit': 'Submit a public tag request for your address, contract, or DApp. Help users identify addresses on %network_title%.',

  // ── L2/Rollup ──
  '/withdrawals': 'Track withdrawal transactions on the %network_title% network.',
  '/txn-withdrawals': 'View L2 to L1 withdrawal messages and their relay status on %network_title%.',
  '/deposits': 'View L1 to L2 deposit transactions on the %network_title% network.',
  '/output-roots': 'View L2 output roots published to L1 on the %network_title% network.',
  '/dispute-games': 'View dispute games and fault proof challenges on the %network_title% network.',
  '/batches': 'View transaction batches submitted on the %network_title% network.',
  '/batches/[number]': 'View details for transaction batch #%number% on the %network_title% network.',
  '/batches/celestia/[height]/[commitment]': 'View Celestia batch at height %height% with commitment %commitment% on %network_title%.',

  // ── Blobs ──
  '/blobs': 'View EIP-4844 blob transactions on the %network_title% network.',
  '/blobs/[hash]': 'View blob %hash% details including data, size, and associated transactions on %network_title%.',

  // ── AA & EIP-7702 ──
  '/aa-transactions': 'View ERC-4337 account abstraction transactions and user operations on the %network_title% network.',
  '/eip7702-authorizations': 'View EIP-7702 authorizations and delegated transactions on the %network_title% network.',

  // ── User Operations ──
  '/ops': 'View user operations (ERC-4337 account abstraction) on the %network_title% network.',
  '/op/[hash]': 'View details for user operation %hash% on the %network_title% network.',

  // ── DEX ──
  '/pools': 'Explore DEX liquidity pools and trading pairs on the %network_title% network.',
  '/pools/[hash]': 'View liquidity pool details, trading volume, and token pair info on %network_title%.',

  // ── Name Domains ──
  '/name-domains': 'Browse registered domain names on the %network_title% network.',
  '/name-domains/[name]': 'View details for domain "%name%" registered on the %network_title% network.',
  '/domain-lookup': 'Search and look up domain names registered on the %network_title% network.',
  '/name-lookup': 'Search and look up domain names registered on the %network_title% network.',

  // ── Validators ──
  '/validators': 'View the top validators ranked by blocks validated on the %network_title% network. Track voting power, daily and weekly block counts.',
  '/validators/[id]': 'View details for validator %id% on the %network_title% network.',
  '/validatorset': 'View validator set snapshots taken at per-block intervals on the %network_title% network. Monitor total voting power and validator counts.',
  '/validatorset/snapshot/[id]': 'View validator set snapshot at block %id% on the %network_title% network.',
  '/validatorsgroup': 'View validator groups with voting power, availability, gas, min assurance and active status on the %network_title% network.',
  '/validatorsgroup/[groupName]': 'View validators list for group %groupName% on the %network_title% network.',

  // ── NFTs ──
  '/nft-top-contracts': 'View top NFT contracts by volume and activity on the %network_title% network.',
  '/nft-top-mints': 'View top NFT mints and collections on the %network_title% network.',
  '/nft-latest-mints': 'View latest NFT mints and new collections on the %network_title% network.',
  '/nft-trades': 'View NFT marketplace trades and sales on the %network_title% network.',
  '/nft-transfers': 'View NFT transfer history across the %network_title% network.',

  // ── Gas ──
  '/gas-tracker': 'Track real-time gas fees on the %network_title% network. Get accurate %network_gwei% estimates for your transactions.',

  // ── Misc Pages ──
  '/advanced-filter': 'Use advanced filters to search for specific transactions, addresses, and tokens on %network_title%.',
  '/csv-export': 'Export blockchain data to CSV format from the %network_title% network.',
  '/visualize/sol2uml': 'Generate UML diagrams from Solidity smart contract source code on %network_title%.',
  '/mud-worlds': 'Explore MUD framework worlds deployed on the %network_title% network.',
  '/interop-messages': 'View cross-chain interoperability messages on the %network_title% network.',
  '/nodetracker': 'View node statistics and geographic distribution across the %network_title% network.',
  '/labelcloud': 'Browse community-contributed labels and tags for addresses on the %network_title% network.',
  '/code-reader': 'Use AI to generate instant answers about smart contract code on %network_title%.',
  '/similar-contracts': 'Find contracts with similar source code deployed on the %network_title% network.',
  '/contract-search': 'Search smart contracts by source code, deployer address, or contract name on %network_title%.',
  '/contract-diff': 'Compare two smart contracts side by side on %network_title% to identify differences.',
  '/404': 'The page you are looking for could not be found on %network_name% Explorer.',

  // ── Tools ──
  '/tools/balance-checker': 'Check the balance of any address on the %network_title% network at a specific block.',
  '/tools/base64': 'Encode and decode Base64 data. A free utility tool by %network_name% Explorer.',
  '/tools/block-date': 'Convert between block numbers and dates on the %network_title% network.',
  '/tools/broadcast-tx': 'Broadcast a signed raw transaction to the %network_title% network.',
  '/tools/csv-export': 'Export transaction and token transfer data to CSV from %network_title%.',
  '/tools/input-decoder': 'Decode transaction input data using ABI or function signature on %network_title%.',
  '/tools/method-id': 'Look up method IDs (function selectors) used in %network_title% smart contracts.',
  '/tools/opcode': 'Analyze EVM opcodes from bytecode on the %network_title% network.',
  '/tools/similar-contracts': 'Find contracts with similar bytecode on the %network_title% network.',
  '/tools/token-approvals': 'Check and revoke token spending approvals on the %network_title% network.',
  '/tools/token-standard': 'Check if a token contract implements a specific ERC standard on %network_title%.',
  '/tools/token-supply': 'Check the total and circulating supply of any token on the %network_title% network.',
  '/tools/tx-decoder': 'Decode raw transaction data on the %network_title% network.',
  '/tools/unit-converter': 'Convert between Wei, Gwei, and Ether units. A utility tool by %network_name% Explorer.',
  '/tools/utf8': 'Convert between UTF-8 text and hexadecimal representations.',
  '/tools/validate-onchain': 'Validate data against on-chain state on the %network_title% network.',
  '/tools/verify-signature': 'Verify cryptographic signatures for messages on the %network_title% network.',
  '/tools/vyper': 'Compile Vyper smart contracts for deployment on the %network_title% network.',

  // ── Company/Info Pages ──
  '/about': 'Learn about %network_name% Explorer, the blockchain explorer for the %network_title% network.',
  '/advertise': 'Advertise on %network_name% Explorer to reach blockchain developers and crypto enthusiasts.',
  '/brand-assets': 'Download official brand assets, logos, and guidelines for %network_name% Explorer.',
  '/bug-bounty': 'Report bugs and vulnerabilities on %network_name% Explorer. Learn about our bug bounty program.',
  '/careers': 'Join the %network_name% Explorer team. View current job openings and career opportunities.',
  '/careers/[slug]': 'Apply for %slug% position at %network_name%. View job details, requirements, and submit your application.',
  '/contact': 'Get in touch with the %network_name% Explorer team. We are here to help.',
  '/priority-support': 'Get priority support for your %network_name% Explorer account and API access.',
  '/privacy-policy': 'Read the %network_name% Explorer privacy policy. Learn how we collect and use your data.',
  '/terms': 'Read the %network_name% Explorer terms of service.',

  // ── Service Routes ──
  '/sprite': DEFAULT_TEMPLATE,
  '/api/metrics': DEFAULT_TEMPLATE,
  '/api/monitoring/invalid-api-schema': DEFAULT_TEMPLATE,
  '/api/log': DEFAULT_TEMPLATE,
  '/api/media-type': DEFAULT_TEMPLATE,
  '/api/proxy': DEFAULT_TEMPLATE,
  '/api/csrf': DEFAULT_TEMPLATE,
  '/api/healthz': DEFAULT_TEMPLATE,
  '/api/config': DEFAULT_TEMPLATE,
  '/api/auth/[...path]': DEFAULT_TEMPLATE,
  '/api/auth/profile/avatar': DEFAULT_TEMPLATE,
};

const TEMPLATE_MAP_ENHANCED: Partial<Record<Route['pathname'], string>> = {
  '/stats/[id]': '%description%',
  '/token/[hash]': 'View %name% (%symbol%) token details, holders, transfers, and price analytics on the %network_title% network.',
  '/address/[hash]': 'View balance, transactions, and contract details for %domain_name% on the %network_title% network.',
};

export function make(pathname: Route['pathname'], isEnriched = false) {
  return (isEnriched ? TEMPLATE_MAP_ENHANCED[pathname] : undefined) ?? TEMPLATE_MAP[pathname] ?? DEFAULT_TEMPLATE;
}
